import { ApiError } from './http';
import { ApiErrorCodes, ClientErrorCodes } from './errors';
import type { ApiErrorCodeLike } from './errors';

/**
 * Retry policy, derived from the backend's error envelope.
 *
 * The rule is "retry what the server said was transient, never what it said was
 * an answer". A 401 or a 404 is an *answer*: retrying it burns a rate-limit
 * budget, delays the re-auth prompt the user needs to see, and (for
 * `invalid_credentials`) can trip an account lockout on the upstream mail host.
 */

/**
 * Codes that are never retried.
 *
 * These are authoritative refusals: the server has answered, and a retry
 * cannot change the outcome. It only burns a rate-limit budget, delays the
 * re-auth prompt the user needs to see, and (for `invalid_credentials`) can
 * trip an account lockout on the upstream mail host. Session expiry is handled
 * by the re-auth flow (WP-15), not by hammering the endpoint.
 *
 * Every code in the generated manifest must appear in exactly one of this set,
 * {@link RETRYABLE_CODES} or {@link SPECIAL_CASE_CODES} — `retry.test.ts`
 * fails otherwise, so a code added upstream cannot reach users unclassified.
 */
export const NON_RETRYABLE_CODES: ReadonlySet<ApiErrorCodeLike> = new Set<ApiErrorCodeLike>([
  ApiErrorCodes.BadRequest,
  ApiErrorCodes.Unauthorized,
  ApiErrorCodes.SessionExpired,
  ApiErrorCodes.SessionInvalid,
  // The revocation epoch moved (a password change) or another session signed
  // this one out. The record is already deleted; a retry sees the same answer.
  ApiErrorCodes.SessionRevoked,
  ApiErrorCodes.InvalidCredentials,
  ApiErrorCodes.Forbidden,
  ApiErrorCodes.DomainNotAllowed,
  // A From that is not the mailbox or one of its aliases (ADR 0099): the same
  // list answers the same way until the composer picks another address.
  ApiErrorCodes.FromNotAllowed,
  // 403 from the image proxy (ADR 0018): the ref was minted for a message the
  // user never opted into, or it names a scored tracker. Neither answer changes
  // on a second attempt — the grant is what changes it, and that is a user
  // action, not a retry.
  ApiErrorCodes.RemoteContentNotAllowed,
  ApiErrorCodes.NotFound,
  ApiErrorCodes.Conflict,
  ApiErrorCodes.StoredCredentialInvalid,
  // Scheduling or snoozing without the stored background credential (ADR
  // 0076): the answer is the enrolment dialog, never another attempt.
  ApiErrorCodes.StoredCredentialRequired,
  // `verify` on a mailbox that signs in with Google or Microsoft (ADR 0109):
  // it is reconnected through the issuer, never with a password.
  ApiErrorCodes.UseOAuth,
  // A replayed key with a different fingerprint, or a duplicate still in
  // flight (ADR 0014). Re-sending the same key can only produce the same 409;
  // the caller decides whether the logical mutation needs a fresh key.
  ApiErrorCodes.IdempotencyKeyReused,
  ApiErrorCodes.MessageTooLarge,
  // A transfer whose target is its own source (ADR 0108).
  ApiErrorCodes.SameAccount,
  ApiErrorCodes.NotImplemented,
  // 501s: the deployment or the mail server cannot provide the feature at all.
  ApiErrorCodes.UnsupportedByHost,
  ApiErrorCodes.UnsupportedByServer,
  ApiErrorCodes.OverQuota,
  // The AI assistant (ADR 0065). Every one is an answer about the user's own
  // Anthropic key or account, and a retry spends their credit: the user
  // decides whether to try again, the transport never does.
  ApiErrorCodes.AINotEnabled,
  ApiErrorCodes.AIKeyRejected,
  ApiErrorCodes.AIModelUnavailable,
  ApiErrorCodes.AIRefused,
  ApiErrorCodes.AINoCredit,
  ApiErrorCodes.AIInputTooLarge,
  ApiErrorCodes.AIRateLimited,
  ApiErrorCodes.AIOverloaded,
  ApiErrorCodes.AIUpstreamError,
  // Login refused under `binding: required` because this browser cannot hold
  // a key, and a malformed or wrong-curve JWK. Neither changes on a retry —
  // both are answered by telling the user what their browser cannot do.
  ApiErrorCodes.DeviceKeyRequired,
  ApiErrorCodes.DeviceKeyInvalid,
  // Server administration (ADRs 0030-0032). Each is the server refusing a
  // routing write on purpose: the admin has to read the answer -- confirm the
  // diff, pick another pattern, keep an administrator routable -- and a second
  // identical request can only be refused identically.
  ApiErrorCodes.ConfirmationRequired,
  ApiErrorCodes.RulePinned,
  ApiErrorCodes.AdminLockoutRisk,
  // Mail labels (ADR 0083). Each is an answer about the label or the folder
  // (a clashing name, the cap, a rename still running, a folder that cannot
  // hold keywords); resending the same request is refused the same way.
  ApiErrorCodes.LabelExists,
  ApiErrorCodes.LabelLimit,
  ApiErrorCodes.LabelBusy,
  ApiErrorCodes.LabelNameTooLong,
  ApiErrorCodes.UnknownLabel,
  ApiErrorCodes.LabelsNotSupported,
  // Drafts (ADR 0100): the version a save was edited from was replaced or
  // removed elsewhere. The same save is refused the same way; the composer
  // decides what to do with its text.
  ApiErrorCodes.DraftChanged,
  // Nextcloud (ADR 0084). Every one is an answer about the person's own
  // Nextcloud or the address they typed -- a refused host, a withdrawn link,
  // a full quota, sharing turned off -- and a second identical request is
  // refused identically. Only `nextcloud_unreachable` is retried.
  ApiErrorCodes.NextcloudURLInvalid,
  ApiErrorCodes.NextcloudHostBlocked,
  ApiErrorCodes.NextcloudNotLinked,
  ApiErrorCodes.NextcloudLinkExpired,
  ApiErrorCodes.NextcloudServerMismatch,
  ApiErrorCodes.NextcloudRejected,
  ApiErrorCodes.NextcloudRefused,
  ApiErrorCodes.NextcloudSharingDisabled,
  ApiErrorCodes.NextcloudQuotaExceeded,
  ApiErrorCodes.NextcloudUploadTooLarge,
  ApiErrorCodes.NextcloudFolderTooLarge,
  ApiErrorCodes.NextcloudNotFound,
  // Web Push (#197): this browser has no live subscription for the test
  // notification; only registering again changes that.
  ApiErrorCodes.PushNotSubscribed,
]);

/**
 * Codes that are worth another attempt: the upstream IMAP/SMTP/DAV hop or our
 * own process failed, or the request never left the browser.
 *
 * `rate_limited` is retryable but capped harder — see
 * {@link MAX_RATE_LIMIT_RETRIES} — and always waits out `Retry-After`.
 */
export const RETRYABLE_CODES: ReadonlySet<ApiErrorCodeLike> = new Set<ApiErrorCodeLike>([
  ApiErrorCodes.Internal,
  ApiErrorCodes.UpstreamFailure,
  ApiErrorCodes.UpstreamBusy,
  ApiErrorCodes.UpstreamTimeout,
  // The per-account circuit breaker is open. It closes on its own, and
  // `details.next_retry_at` says roughly when.
  ApiErrorCodes.AccountDegraded,
  ApiErrorCodes.RateLimited,
  // The person's Nextcloud did not answer (network, TLS, timeout). A chunk
  // re-PUT is idempotent by construction, so the upload row offers Retry.
  ApiErrorCodes.NextcloudUnreachable,
  ClientErrorCodes.Network,
]);

/**
 * Codes whose recovery is not "try the same request again", and which
 * therefore belong to neither set above.
 *
 * - `csrf_invalid`: the transport re-primes the cookie and retries once,
 *   inside `HttpTransport.request`. By the time an `ApiError` escapes with
 *   this code, that retry has already been spent.
 * - `folder_invalidated`: UIDVALIDITY changed under a cursor. The cached list
 *   is meaningless, so the caller drops it and refetches from the first page
 *   (doc 10 §1.5); replaying the stale cursor would 409 forever.
 */
export const SPECIAL_CASE_CODES: ReadonlySet<ApiErrorCodeLike> = new Set<ApiErrorCodeLike>([
  ApiErrorCodes.CsrfInvalid,
  ApiErrorCodes.FolderInvalidated,
  // The four `proof_*` codes whose recovery is not "send it again":
  //
  // - `proof_required` — this browser has no key. The answer is the in-place
  //   password dialog (ADR 0042), not another unsigned request.
  // - `proof_invalid` — usually a sibling tab rebound the session; the
  //   transport reloads the key handle and spends its one retry there.
  // - `proof_stale` — the clock is wrong; the transport re-syncs from
  //   `details.server_time` and spends its one retry there.
  // - `proof_replayed` — the `jti` was spent, almost always by a copy of this
  //   very request that a proxy or the browser re-sent on a dropped
  //   connection. The transport signs a fresh proof and spends its one retry
  //   there; the identical proof is never sent again.
  //
  // By the time an `ApiError` with one of these escapes, that retry is gone,
  // and a query-level retry would only produce the same refusal while
  // delaying the dialog the user actually needs.
  ApiErrorCodes.ProofRequired,
  ApiErrorCodes.ProofInvalid,
  ApiErrorCodes.ProofStale,
  ApiErrorCodes.ProofReplayed,
  // RFC 8470: the request arrived in TLS early data and was refused before
  // anything ran. The browser normally re-sends it after the handshake; when
  // one hands it back instead, the transport re-signs and retries once.
  ApiErrorCodes.TooEarly,
  // The ticket was consumed, expired or never existed. `connection.ts` mints
  // a *new* one — reusing this one is the single thing ADR 0041 forbids.
  ApiErrorCodes.StreamTicketInvalid,
  // An admin write with no live elevation (ADR 0032). The recovery is a
  // password prompt and ONE resend with the same Idempotency-Key, which the
  // transport performs through `onStepUpRequired`; a query-level retry would
  // only produce the same 403 without ever asking for the password.
  ApiErrorCodes.StepUpRequired,
]);

/** Attempts after the first, for a normal transient failure. */
export const MAX_QUERY_RETRIES = 2;
/** A 429 gets exactly one honoured `Retry-After` wait before we give up. */
export const MAX_RATE_LIMIT_RETRIES = 1;

const BASE_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
/** A server-supplied `Retry-After` longer than this is not worth holding a query for. */
export const MAX_RETRY_AFTER_MS = 60_000;

/** True when this failure could plausibly succeed on another attempt. */
export function isRetryableError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    // Not an envelope failure at all (a bug in a queryFn, an AbortError, a
    // TypeError). Retrying cannot help and hides the stack trace.
    return false;
  }
  if (NON_RETRYABLE_CODES.has(error.code)) return false;
  if (SPECIAL_CASE_CODES.has(error.code)) return false;
  if (RETRYABLE_CODES.has(error.code)) return true;
  // Unknown or synthesised code (`request_failed`): fall back to the status.
  // 0 is "never reached the server", 5xx is "the server broke", 429 is "later".
  return error.status === 0 || error.status === 429 || error.status >= 500;
}

/** `retry` for queries: honours the envelope, and caps 429s harder than the rest. */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (!isRetryableError(error)) return false;
  if (error instanceof ApiError && error.isRateLimited) {
    return failureCount <= MAX_RATE_LIMIT_RETRIES;
  }
  return failureCount <= MAX_QUERY_RETRIES;
}

/**
 * `retryDelay`: a rate-limited response dictates the wait, everything else gets
 * exponential backoff with jitter so a fleet of tabs does not resynchronise
 * onto the same retry instant.
 */
export function retryDelay(failureCount: number, error: unknown): number {
  if (error instanceof ApiError && error.retryAfter !== undefined) {
    return Math.min(error.retryAfter * 1_000, MAX_RETRY_AFTER_MS);
  }
  const exponential = Math.min(BASE_BACKOFF_MS * 2 ** Math.max(failureCount - 1, 0), MAX_BACKOFF_MS);
  // Full jitter over the top half of the window: never shorter than half the
  // backoff, never longer than the backoff itself.
  return Math.round(exponential / 2 + Math.random() * (exponential / 2));
}

/**
 * `retry` for mutations. Mail mutations are not all idempotent (`archive`,
 * `remove`, `send`), and the optimistic layer has already painted the result,
 * so a silent retry would risk double-applying a server-side action. Retrying
 * is an explicit user affordance — the "Retry" action on the failure toast.
 *
 * ADR 0014 makes a *deliberate* retry safe — the backend now honours the
 * `Idempotency-Key` the transport already sends, and replays the stored
 * response instead of re-executing — but "safe to retry on purpose" is not
 * "retry silently": the optimistic layer has already painted the result, and
 * an automatic retry would hide a failure the user should see.
 */
export const mutationRetry = false;
